import { useState } from "react";
import { Input, Select } from "antd";
import { get } from "lodash";
import PropTypes from "prop-types";
import { looksLikeNeed } from "./needs-fields";

export const CUSTOM_ENTITY = "custom";

export const FIELD_TYPES = [
  "text",
  "textarea",
  "email",
  "phone",
  "date",
  "number",
  "slider",
  "select",
  "checkbox",
  "radioGroup",
  "file",
];

const OPTION_TYPES = ["select", "radioGroup"];

const KEY_PATTERN = /^[a-z][a-zA-Z0-9]*$/;
const MAX_KEY_LENGTH = 48;
const OPTION_SEPARATOR = "|";
const KEY_FALLBACK_PREFIX = "field";

const TYPE_BY_INPUT_KIND = {
  text: "text",
  textarea: "textarea",
  email: "email",
  tel: "phone",
  phone: "phone",
  date: "date",
  number: "number",
  slider: "slider",
  range: "slider",
  select: "select",
  checkbox: "checkbox",
  toggle: "checkbox",
  radioGroup: "radioGroup",
  radio: "radioGroup",
  file: "file",
};

const capitalize = (word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`;

export const isCustomPath = (path) =>
  String(path || "").startsWith(`${CUSTOM_ENTITY}.`);

export const customKeyOf = (path) =>
  isCustomPath(path) ? path.slice(CUSTOM_ENTITY.length + 1) : null;

export const keyFromLabel = (label) => {
  const words = String(label || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^A-Za-z0-9]+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean)
    .map((word) => word.toLowerCase());
  const key = words
    .map((word, index) => (index === 0 ? word : capitalize(word)))
    .join("")
    .slice(0, MAX_KEY_LENGTH);
  if (!key) return "";
  return /^[a-z]/.test(key) ? key : `${KEY_FALLBACK_PREFIX}${capitalize(key)}`;
};

const optionsText = (options) =>
  (options || [])
    .filter((option) => get(option, "value"))
    .map((option) =>
      option.label && option.label !== option.value
        ? `${option.value} ${OPTION_SEPARATOR} ${option.label}`
        : option.value,
    )
    .join("\n");

export const parseOptions = (text) =>
  String(text || "")
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const [value, ...rest] = line.split(OPTION_SEPARATOR);
      const trimmed = value.trim();
      return {
        value: trimmed,
        label: rest.join(OPTION_SEPARATOR).trim() || trimmed,
      };
    })
    .filter((option) => option.value);

const knownType = (type) => (FIELD_TYPES.indexOf(type) >= 0 ? type : null);

export const fieldDraftOf = (definition, key) => ({
  label: get(definition, "label") || key,
  key,
  type:
    knownType(get(definition, "type")) ||
    (get(definition, "options.length") ? "select" : FIELD_TYPES[0]),
  options: optionsText(get(definition, "options")),
});

export const fieldDraftFor = (slot) => {
  const entity = get(slot, "binding.entity");
  const field = get(slot, "binding.field") || "";
  const label = get(slot, "label") || "";
  const ownKey = entity === CUSTOM_ENTITY && KEY_PATTERN.test(field);
  return {
    label,
    key: ownKey ? field : keyFromLabel(label) || keyFromLabel(field),
    type: TYPE_BY_INPUT_KIND[get(slot, "inputKind")] || FIELD_TYPES[0],
    options: optionsText(get(slot, "radioOptions")),
  };
};

const WorkshopFieldCreate = ({
  labels,
  initial,
  editing,
  scopeNote,
  disabled,
  onSubmit,
  onCancel,
}) => {
  const [draft, setDraft] = useState(initial);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const key = draft.key.trim();
  const label = draft.label.trim();
  const withOptions = editing || OPTION_TYPES.includes(draft.type);
  const options = withOptions ? parseOptions(draft.options) : [];
  const keyValid = KEY_PATTERN.test(key);
  const needLike = !editing && (looksLikeNeed(key) || looksLikeNeed(label));
  const ready =
    !needLike &&
    keyValid &&
    Boolean(label) &&
    (editing || !withOptions || options.length);
  const type = draft.type;
  const off = disabled || submitting;

  const change = (name) => (event) =>
    setDraft((current) => ({ ...current, [name]: event.target.value }));

  const submit = async () => {
    setSubmitting(true);
    setError("");
    const failure = await onSubmit({ key, label, type, options });
    if (!failure) return;
    setSubmitting(false);
    setError(failure);
  };

  return (
    <div className="workshop-quick__form">
      <span className="workshop-quick__label">{labels.TITLE}</span>
      <span className="workshop-quick__note">{scopeNote}</span>
      <label className="workshop-quick__question">
        <span className="workshop-quick__label workshop-quick__label--small">
          {labels.LABEL}
        </span>
        <Input value={draft.label} disabled={off} onChange={change("label")} />
      </label>
      <label className="workshop-quick__question">
        <span className="workshop-quick__label workshop-quick__label--small">
          {labels.KEY}
        </span>
        <Input
          addonBefore={`${CUSTOM_ENTITY}.`}
          value={draft.key}
          disabled={off || editing}
          status={key && !keyValid ? "error" : undefined}
          onChange={change("key")}
        />
        <span className="workshop-quick__note">
          {editing
            ? labels.KEY_LOCKED
            : key && !keyValid
              ? labels.KEY_INVALID
              : labels.KEY_HINT}
        </span>
      </label>
      {needLike ? (
        <span role="alert" className="workshop-quick__error">
          {labels.NEEDS_RESERVED}
        </span>
      ) : null}
      <div className="workshop-quick__question">
        <span className="workshop-quick__label workshop-quick__label--small">
          {labels.TYPE}
        </span>
        <Select
          virtual={false}
          className="workshop-select"
          aria-label={labels.TYPE}
          disabled={off}
          value={draft.type}
          options={FIELD_TYPES.map((value) => ({
            value,
            label: get(labels, ["TYPES", value]) || value,
          }))}
          onChange={(next) =>
            setDraft((current) => ({ ...current, type: next }))
          }
        />
      </div>
      {withOptions ? (
        <label className="workshop-quick__question">
          <span className="workshop-quick__label workshop-quick__label--small">
            {labels.OPTIONS}
          </span>
          <Input.TextArea
            autoSize={{ minRows: 3, maxRows: 8 }}
            placeholder={labels.OPTIONS_PLACEHOLDER}
            value={draft.options}
            disabled={off}
            onChange={change("options")}
          />
          <span className="workshop-quick__note">{labels.OPTIONS_HINT}</span>
        </label>
      ) : null}
      {error ? (
        <span role="alert" className="workshop-quick__error">
          {error}
        </span>
      ) : null}
      <div className="workshop-quick__footer">
        <button
          type="button"
          className="workshop-quick__primary"
          disabled={off || !ready}
          onClick={submit}
        >
          {labels.SUBMIT}
        </button>
        <button
          type="button"
          className="workshop-quick__secondary"
          disabled={off}
          onClick={onCancel}
        >
          {labels.CANCEL}
        </button>
      </div>
    </div>
  );
};

WorkshopFieldCreate.propTypes = {
  labels: PropTypes.object.isRequired,
  initial: PropTypes.shape({
    label: PropTypes.string,
    key: PropTypes.string,
    type: PropTypes.string,
    options: PropTypes.string,
  }).isRequired,
  editing: PropTypes.bool,
  scopeNote: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

WorkshopFieldCreate.defaultProps = {
  editing: false,
  disabled: false,
};

export default WorkshopFieldCreate;
